import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import ReactSVG from 'react-svg';

import { Row, Col } from 'components/Styled/Basic';
import CaptionContainer, { useCaptionContainer } from '../utils/CaptionContainer';
import { useAnimationDelay } from '../utils/useDelay';

const CustomContainer = styled(CaptionContainer)`
	display: flex;
	flex-direction: column;
	background: white;
	border-radius: 3px;
	box-shadow: 0 0 54px 4px rgba(0, 0, 0, 0.5);
	transition: opacity .2s ease;
	opacity: ${(props) => (props.$isOpened ? '1' : '0')};
`;

export const Content = styled.div`
	display: flex;
	flex-direction: column;
	flex: 1;
	box-sizing: border-box;
`;

export const Head = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: space-between;
	align-items: center;
	min-height: 48px;
	padding: 0 22px;
	background: black;
	color: white;
	cursor: grab;
	
	svg {
		width: 16px;
		height: 16px;
		fill: white;
	}
`;

export const ResizeButton = styled.div`
	position: absolute;
	right: 0;
	bottom: 0;
	width: 15px;
	height: 15px;
	cursor: ${(props) => props.cursor};
	
	svg {
		width: 15px;
		height: 15px;
	}
`;

export const CloseIcon = styled.div`
	cursor: pointer;
`;

const Popup = (props) => {
	const {
		id,
		children,
		title,
		isShow,
		onClose,
		onClickClose,
		width,
		height,
		className,
		noResize,
		rowHeight,
		renderHead: Head,
		renderContent: Content,
		showClose,
	} = props;

	const { containerProps, positionEvents, sizeEvents } = useCaptionContainer(id, {
		width,
		height,
		noResize,
	});

	const {
		isActive,
		isOpened,
		handleClose,
		handleOpen,
	} = useAnimationDelay(({ isActive, isOpened }) => {
		if (!isActive && !isOpened && typeof onClose === 'function') {
			onClose();
		}
	});

	const handleClickClose = () => {
		if (typeof onClickClose === 'function') {
			return onClickClose();
		}

		handleClose();
	};

	useEffect(() => {
		if (isShow) {
			handleOpen();
		}

		if (!isShow) {
			handleClose();
		}
	}, [isShow]);

	if (!isActive) {
		return null;
	}

	return (
		<CustomContainer
			$isOpened={isOpened}
			{...containerProps}
			className={className}
		>
		<Head {...positionEvents}>
			<span>{title}</span>

			{showClose && (
				<CloseIcon onClick={handleClickClose}>
					<ReactSVG src="/data/svg/close.svg" />
				</CloseIcon>
			)}
		</Head>

			<Row height={rowHeight} overflow="auto">
				<Content>
					{typeof children === 'function' ? children({ handleClose }) : children}
				</Content>
			</Row>

			{!noResize && (
				<ResizeButton cursor="nwse-resize" {...sizeEvents}>
					<ReactSVG src="/data/svg/resize.svg" />
				</ResizeButton>
			)}
		</CustomContainer>
	);
};

Popup.propTypes = {
	id: PropTypes.string.isRequired,
	children: PropTypes.oneOfType([
		PropTypes.node,
		PropTypes.func,
	]).isRequired,
	title: PropTypes.node,
	isShow: PropTypes.bool.isRequired,
	onClose: PropTypes.func,
	width: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),
	height: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number,
	]),

	className: PropTypes.string,
	renderHead: PropTypes.any,
	renderContent: PropTypes.any,
	noResize: PropTypes.bool,
	rowHeight: PropTypes.any,
	showClose: PropTypes.bool,
};

Popup.defaultProps = {
	title: '',
	width: 'fit-content',
	height: 'fit-content',
	renderHead: Head,
	renderContent: Content,
	showClose: true,
};

export default Popup;
